import { useAddItemMutation, useDeleteItemMutation, useFetchItem, useFetchItems, useUpdateItemMutation } from "./base-hooks";
import { CacheKey } from "../types/cache-keys";
import { Product, ProductAssignmentPayload } from "../types/product";
import { deleteItem, fetchItems, postItem } from "../support/queries";
//-tech-debt-react-query;
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { DEFAULT_QUERY_OPTIONS } from "../support/constants";
import { Opportunity, OpportunityArchitecture, OpportunityChallenge, OpportunityOutcome, OpportunityProduct } from "../types/opportunity";
import { Challenge, Outcome } from "../types/customer";
import { v4 as uuidv4 } from 'uuid';
import { PartialWithId } from "../types/base-types";

// ---- Fetches ----
const useFetchOpportunity = (id, options?) => useFetchItem<Opportunity>('sf/opportunity', id, CacheKey.Opportunity, {}, {
  ...options,
  select: (data) => ({
    ...data,
    id: data?.Id ?? data?.id,
    organizational_priority: data?.organizational_priority ?? data?.Organizational_Priority__c ?? null,
    solution_type: data?.solution_type ?? data?.Solution_Type__c ?? null
  })
});
const useFetchOpportunityProducts = (opportunityId: string, params?, options?) => useFetchItems<Product[]>(`db/opportunity/${opportunityId}/product`, () => [CacheKey.OpportunityProduct, opportunityId], params, options);
const useFetchOpportunityProductAssignments = (
  params?,
  options?
) => useFetchItems('db/opportunity-product', () => [CacheKey.OpportunityProductAssignment, ...(params ? [params.opportunity] : [])], params, options);
const useFetchOpportunityProductAssignment = (id: number, opportunityId: string, options?) => useFetchItem<OpportunityProduct>(`db/opportunity-product`, id, () => [CacheKey.OpportunityProductAssignment, opportunityId], {}, options);

const useFetchOpportunityArchitectures = (id, options?) => useFetchItems<OpportunityArchitecture[]>(`db/opportunity-architecture`, () => [CacheKey.OpportunityArchitectures, id], { opportunity: id }, options);
const useFetchOpportunityChallenges = (id, options?) => useFetchItems<OpportunityChallenge[]>(`db/opportunity-challenge`, () => [CacheKey.OpportunityChallenges, id], { opportunity: id }, options);
const useFetchOpportunityOutcomes = (id, options?) => useFetchItems<OpportunityOutcome[]>(`db/opportunity-outcome`, () => [CacheKey.OpportunityOutcomes, id], { opportunity: id }, options);

// using table query for getting the deal id from sf
// there must be a better way to get deal id, maybe including in opp fields anyway
const useFetchOpportunityDeal = (id) => useQuery({
  queryKey: [CacheKey.OpportunityDeal, id],
  queryFn: () => fetchItems(
    'sf/opportunity',
    { page: 0, size: 1 },
    {
      ...DEFAULT_QUERY_OPTIONS,
      pagination: true,
      body: {
        filters: [
          {
            prop: 'Opportunity_ID_18__c',
            operator: 'is',
            comparison: id,
          },
        ],
        order_by: 'Name ASC',
        columns: ['Opportunity_ID_18__c', 'DealID__c'],
        search_string: '',
      },
    },
  ),
});

const useFetchOpportunityActivityHistory = (params, options?) => useQuery({
  queryKey: [CacheKey.OpportunityActivityHistory, params.opportunity],
  queryFn: () => fetchItems(`sf/query`, {
    q: `SELECT ActivityDate, Description, WhatId, Status, Subject, OwnerId, Priority FROM Task WHERE WhatId = '${params.opportunity}' ORDER BY CreatedDate DESC`,
  }),
  ...{
      ...DEFAULT_QUERY_OPTIONS,
      enabled: !!params.opportunity,
    },
});

const useFetchCustomer = (id, options?) => useFetchItem('sf/customer', id, CacheKey.Customer, options);

// ---- Mutations ----
const useAddOpportunityProductAssignmentMutation = () => useAddItemMutation<Partial<Product>>(
  'db/opportunity-product',
  (payload: any & { opportunity: Opportunity['id'] }) => [CacheKey.OpportunityProductAssignment, payload.opportunity]
);
const useUpdateOpportunityProductAssignmentMutation = () => useUpdateItemMutation<OpportunityProduct>(
  'db/opportunity-product',
  (payload) => [CacheKey.OpportunityProductAssignment, payload.opportunity, payload.id]
);

const useAddOpportunityProductAssignmentsMutation = () => {
  return async (productPayloads: ProductAssignmentPayload[]) => {
    await Promise.all(productPayloads.map(payload => postItem('db/opportunity-product', payload)));
  };
}

const useDeleteOpportunityProductAssignmentsMutation = () => {
  return async (assignmentIds: number[]) => {
    await Promise.all(assignmentIds.map(id => deleteItem(`db/opportunity-product/${id}`)));
  };
}

const useDeleteOpportunityProductAssignmentMutation = () => useDeleteItemMutation<PartialWithId<OpportunityProduct> & { opportunity: Opportunity['id'] }>(
  'db/opportunity-product',
  (payload) => [CacheKey.OpportunityProductAssignment, payload.opportunity, payload.id]
);

interface ChallengeMapping {
  id: number;
  challenge: Challenge;
  opportunity: Opportunity;
  last_updated?: Date;
  updated_by?: string;
}

// have to provide a bespoke mutation since we a passing one object structure, and recieving a different object structure. Standard mutation approach won't work currently
const useAddOpportunityChallengeMutation = () => {
  const queryClient = useQueryClient();

  return useMutation<
    ChallengeMapping,
    Error,
    Omit<ChallengeMapping, 'id'> & { opportunity?: Opportunity; challenge?: Challenge },
    {
      previous: ChallengeMapping[] | undefined;
      arrayExists: boolean;
      arrayQueryKey: string[];
    }
  >({
    mutationFn: (payload) =>
      postItem('db/opportunity-challenge', {
        opportunity_id: payload.opportunity?.id,
        challenge_id: payload.challenge?.id,
        updated_by: payload.updated_by,
      }),
    onMutate: async (created) => {
      const arrayQueryKey = [CacheKey.OpportunityChallenges, created.opportunity.id];
      await queryClient.cancelQueries({ queryKey: arrayQueryKey });
      const previous = queryClient.getQueryData<ChallengeMapping[]>(arrayQueryKey) || undefined;
      const arrayExists = queryClient.getQueryData<ChallengeMapping[]>(arrayQueryKey) !== undefined;

      queryClient.setQueryData<ChallengeMapping[]>(arrayQueryKey, [
        ...(previous || []),
        {
          id: Number(uuidv4()),
          challenge: created.challenge ?? null,
          opportunity: created.opportunity ?? null,
        },
      ]);

      return { previous, arrayExists, arrayQueryKey };
    },
    onSuccess: (response, _variables, context) => {
      queryClient.setQueryData<ChallengeMapping[]>(context.arrayQueryKey, [
        ...(context.previous ?? []),
        response,
      ]);
    },
    onError: (_error, _variables, context) => {
      if (context?.arrayExists) {
        queryClient.setQueryData(context.arrayQueryKey, context.previous);
      } else if (context) {
        queryClient.removeQueries({ queryKey: context.arrayQueryKey });
      }
    },
    onSettled: (_response, _error, _variables, context) => {
      if (context) queryClient.invalidateQueries({ queryKey: context.arrayQueryKey });
    },
  })
};
const useDeleteOpportunityChallengeMutation = () => useDeleteItemMutation<{ opportunity: Opportunity['id'], id: number }>(
  'db/opportunity-challenge',
  (payload) => [CacheKey.OpportunityChallenges, payload.opportunity, payload.id]
);

interface OutcomeMapping {
  id: number;
  outcome: Outcome;
  opportunity: Opportunity;
  last_updated?: Date;
  updated_by?: string;
}

// have to provide a bespoke mutation since we a passing one object structure, and recieving a different object structure. Standard mutation approach won't work currently
const useAddOpportunityOutcomeMutation = () => {
  const queryClient = useQueryClient();

  return useMutation<
    OutcomeMapping,
    Error,
    Omit<OutcomeMapping, 'id'> & { opportunity?: Opportunity; outcome?: Outcome },
    {
      previous: OutcomeMapping[] | undefined;
      arrayExists: boolean;
      arrayQueryKey: string[];
    }
  >({
    mutationFn: (payload) =>
      postItem('db/opportunity-outcome', {
        opportunity_id: payload.opportunity?.id,
        outcome_id: payload.outcome?.id,
        updated_by: payload.updated_by,
      }),
    onMutate: async (created) => {
      const arrayQueryKey = [CacheKey.OpportunityOutcomes, created.opportunity.id];
      await queryClient.cancelQueries({ queryKey: arrayQueryKey });
      const previous = queryClient.getQueryData<OutcomeMapping[]>(arrayQueryKey) || undefined;
      const arrayExists = queryClient.getQueryData<OutcomeMapping[]>(arrayQueryKey) !== undefined;

      queryClient.setQueryData<OutcomeMapping[]>(arrayQueryKey, [
        ...(previous || []),
        {
          id: Number(uuidv4()),
          outcome: created.outcome ?? null,
          opportunity: created.opportunity ?? null,
        },
      ]);

      return { previous, arrayExists, arrayQueryKey };
    },
    onSuccess: (response, _variables, context) => {
      queryClient.setQueryData<OutcomeMapping[]>(context.arrayQueryKey, [
        ...(context.previous ?? []),
        response,
      ]);
    },
    onError: (_error, _variables, context) => {
      if (context?.arrayExists) {
        queryClient.setQueryData(context.arrayQueryKey, context.previous);
      } else if (context) {
        queryClient.removeQueries({ queryKey: context.arrayQueryKey });
      }
    },
    onSettled: (_response, _error, _variables, context) => {
      if (context) queryClient.invalidateQueries({ queryKey: context.arrayQueryKey });
    },
  })
};

const useDeleteOpportunityOutcomeMutation = () => useDeleteItemMutation<{ opportunity: Opportunity['id'], id: number }>(
  'db/opportunity-outcome',
  (payload) => [CacheKey.OpportunityOutcomes, payload.opportunity, payload.id]
);

const useAddOpportunityArchitectureMutation = () => useAddItemMutation<OpportunityArchitecture>(
  'db/opportunity-architecture',
  (payload) => [CacheKey.OpportunityArchitectures, payload.opportunity]
);
const useDeleteOpportunityArchitectureMutation = () => useDeleteItemMutation<{ opportunity: Opportunity['id'], id: number }>(
  'db/opportunity-architecture',
  (payload) => [CacheKey.OpportunityArchitectures, payload.opportunity, payload.id]
);

const useUpdateOpportunityMutation = () => useUpdateItemMutation<Partial<Opportunity> & { id: Opportunity['id'] }>(
  'db/opportunity',
  (payload) => [CacheKey.Opportunity, payload.id]
);


export {
  useFetchOpportunity,
  useFetchOpportunityProducts,
  useFetchOpportunityProductAssignments,
  useFetchOpportunityProductAssignment,
  useFetchOpportunityOutcomes,
  useFetchOpportunityChallenges,
  useFetchOpportunityArchitectures,
  useFetchOpportunityActivityHistory,
  useFetchOpportunityDeal,

  useFetchCustomer,

  useAddOpportunityProductAssignmentMutation,
  useAddOpportunityProductAssignmentsMutation,
  useUpdateOpportunityProductAssignmentMutation,
  useDeleteOpportunityProductAssignmentMutation,
  useDeleteOpportunityProductAssignmentsMutation,

  useAddOpportunityChallengeMutation,
  useDeleteOpportunityChallengeMutation,

  useAddOpportunityOutcomeMutation,
  useDeleteOpportunityOutcomeMutation,

  useAddOpportunityArchitectureMutation,
  useDeleteOpportunityArchitectureMutation,

  useUpdateOpportunityMutation
};